/**
 * Shared employee types for API contracts between frontend and backend
 */
import { z } from "zod";
import { uuidSchema, dateSchema } from "./validation-common.js";
// ===== VALIDATION SCHEMAS =====
/**
 * Employee filters for GET /api/employees
 * All filters are optional and use AND logic (must meet ALL criteria if provided)
 */
export const EmployeeFiltersSchema = z.object({
    locationId: uuidSchema.optional().nullable(),
    locationIds: z.array(uuidSchema).optional().nullable(),
    jobRoleIds: z.array(uuidSchema).optional().nullable(),
    skillIds: z.array(uuidSchema).optional().nullable(),
    timeRangeStart: dateSchema.optional().nullable(),
    timeRangeEnd: dateSchema.optional().nullable(),
}).refine((data) => {
    // If both times provided, end must be after start
    if (data.timeRangeStart && data.timeRangeEnd) {
        return data.timeRangeEnd > data.timeRangeStart;
    }
    return true;
}, {
    message: "Time range end must be after start",
    path: ["timeRangeEnd"],
});
/**
 * Query schema for GET /api/employees
 * Requires companyId, optionally filters by location, job roles, skills, and time availability
 * Supports text search by employee name
 */
export const GetEmployeesQuerySchema = z.object({
    companyId: uuidSchema,
    locationId: uuidSchema.optional().nullable(),
    jobRoleIds: z.string().transform(val => val.split(',')).pipe(z.array(uuidSchema)).optional().nullable(),
    skillIds: z.string().transform(val => val.split(',')).pipe(z.array(uuidSchema)).optional().nullable(),
    search: z.string().optional(),
    timeRangeStart: dateSchema.optional().nullable(),
    timeRangeEnd: dateSchema.optional().nullable(),
    isActive: z.stringbool({
        truthy: ["true"],
        falsy: ["false"]
    }).optional(),
}).refine((data) => {
    // Either both or neither time range must be provided
    const hasStart = Boolean(data.timeRangeStart);
    const hasEnd = Boolean(data.timeRangeEnd);
    return hasStart === hasEnd;
}, {
    message: "Both timeRangeStart and timeRangeEnd must be provided together",
    path: ["timeRangeStart"],
}).refine((data) => {
    // If both times provided, end must be after start
    if (data.timeRangeStart && data.timeRangeEnd) {
        return data.timeRangeEnd > data.timeRangeStart;
    }
    return true;
}, {
    message: "Time range end must be after start",
    path: ["timeRangeEnd"],
});
// ===== oRPC INPUT SCHEMA =====
/**
 * Input schema for oRPC list employees endpoint
 * Like GetEmployeesQuerySchema but WITHOUT companyId (comes from JWT)
 * and uses z.iso.datetime() instead of z.coerce.date() for oRPC compatibility
 */
export const ListEmployeesInputSchema = z.object({
    locationId: uuidSchema.optional().nullable(),
    locationIds: z.string().transform(val => val.split(',')).pipe(z.array(uuidSchema)).optional().nullable(),
    jobRoleIds: z.string().transform(val => val.split(',')).pipe(z.array(uuidSchema)).optional().nullable(),
    skillIds: z.string().transform(val => val.split(',')).pipe(z.array(uuidSchema)).optional().nullable(),
    search: z.string().optional(),
    timeRangeStart: z.iso.datetime().optional().nullable(),
    timeRangeEnd: z.iso.datetime().optional().nullable(),
    isActive: z.stringbool({
        truthy: ["true"],
        falsy: ["false"]
    }).optional(),
});
// ===== RESPONSE SCHEMAS =====
/**
 * Location summary for employee roster
 */
export const EmployeeLocationResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    city: z.string(),
    state: z.string(),
});
/**
 * Job role summary for employee roster
 */
export const EmployeeJobRoleResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
});
/**
 * Skill summary for employee roster
 */
export const EmployeeRosterSkillSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    expirationDate: z.iso.datetime().nullable(),
    expirationStatus: z.enum(["normal", "expiring", "expired"]),
});
/**
 * Employee information returned by list endpoint
 * Includes data needed for company roster display
 */
export const EmployeeResponseSchema = z.object({
    id: z.uuid(),
    employmentId: z.uuid(),
    firstName: z.string(),
    lastName: z.string(),
    photoUrl: z.string().nullable(),
    employmentStartDate: z.iso.datetime(),
    employmentEndDate: z.iso.datetime().nullable(),
    locations: z.array(EmployeeLocationResponseSchema),
    jobRoles: z.array(EmployeeJobRoleResponseSchema),
    skills: z.array(EmployeeRosterSkillSchema),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
});
